import { DOCUMENT, Inject, Injectable } from "@angular/core";
import { EventManagerPlugin } from "@angular/platform-browser";
import * as Hammer from "hammerjs";

/**
 * Restores the `(tap)`, `(press)` and `(pressup)` template bindings.
 *
 * Angular 22 removed `HammerModule` and every Hammer export from
 * `@angular/platform-browser`. Without this, 32 gesture bindings across the app
 * stop firing with no compile error. `(pressup)` matters most: it is the stop
 * half of press-and-hold, so losing it leaves the weight and BCS steppers
 * incrementing every 120ms with no way to stop (see MetricAddComponent.onPressUp).
 *
 * One Hammer manager per element, not per binding. These controls carry `(tap)`,
 * `(press)` and `(pressup)` on the same button, and separate managers cannot
 * cross-recognise, so a long press would emit `tap` as well as `press` and add a
 * spurious increment. `HammerModule` shared one manager per element; this does
 * the same, reference-counted so the last listener removed tears it down.
 */
const PRESS_EVENTS = ["press", "pressup"] as const;
const SUPPORTED_GESTURES = ["tap", ...PRESS_EVENTS] as const;

interface ManagedElement {
  manager: any;
  listeners: number;
}

@Injectable()
export class HammerGesturePlugin extends EventManagerPlugin {
  private readonly managers = new WeakMap<HTMLElement, ManagedElement>();

  constructor(@Inject(DOCUMENT) doc: any) {
    super(doc);
  }

  supports(eventName: string): boolean {
    return (SUPPORTED_GESTURES as readonly string[]).includes(eventName.toLowerCase());
  }

  addEventListener(element: HTMLElement, eventName: string, handler: (event: unknown) => void): () => void {
    const zone = this.manager.getZone();
    const name = eventName.toLowerCase();

    return zone.runOutsideAngular(() => {
      const managed = this.acquire(element, name);
      const listener = (event: unknown) => zone.run(() => handler(event));
      managed.manager.on(name, listener);

      return () => {
        managed.manager.off(name, listener);
        managed.listeners -= 1;
        if (managed.listeners <= 0) {
          managed.manager.destroy();
          this.managers.delete(element);
        }
      };
    });
  }

  /** One manager per element, with the recognizer this event needs added once. */
  private acquire(element: HTMLElement, name: string): ManagedElement {
    let managed = this.managers.get(element);
    if (!managed) {
      managed = { manager: new (Hammer as any).Manager(element), listeners: 0 };
      this.managers.set(element, managed);
    }
    managed.listeners += 1;

    // A single Press recognizer emits both `press` and `pressup`.
    const recognizer = (PRESS_EVENTS as readonly string[]).includes(name) ? "press" : "tap";
    if (!managed.manager.get(recognizer)) {
      managed.manager.add(
        recognizer === "press" ? new (Hammer as any).Press() : new (Hammer as any).Tap(),
      );
    }
    return managed;
  }
}
